import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { store } from '../store/store';
import Logger from '../common/logger';
import { bookingApiSlice } from '../api/booking/bookingApiSlice';
import { bookingArchiveApiSlice } from '../api/booking/bookingArchiveApiSlice';
import { enqueueErrorSnackbar, enqueueSuccessSnackbar } from '../action/snackbarCommands';

// Tags live on bookingApiSlice (legacy backend). After a successful
// mutation on the new-backend archive slice we manually invalidate them
// here so the list + details refetch — same cross-slice pattern as
// seriesBookingApiSlice.
const invalidateBookingTags = () => {
    store.dispatch(
        bookingApiSlice.util.invalidateTags(['bookingList', 'bookingDetails']),
    );
};

export const useArchiveBooking = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation();

    return async (bookingId: string, reason?: string, note?: string): Promise<boolean> => {
        try {
            await store
                .dispatch(bookingArchiveApiSlice.endpoints.archiveBooking.initiate({ bookingId, reason, note }))
                .unwrap();
            invalidateBookingTags();
            dispatch(enqueueSuccessSnackbar({ message: t('bookings.archive.snackbar.archive_success') }));
            return true;
        } catch (error) {
            Logger('archiveBooking').error(error);
            dispatch(enqueueErrorSnackbar({ message: t('bookings.archive.snackbar.archive_error') }));
            return false;
        }
    };
};

export const useRestoreBooking = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation();

    return async (bookingId: string): Promise<boolean> => {
        try {
            await store
                .dispatch(bookingArchiveApiSlice.endpoints.restoreBooking.initiate({ bookingId }))
                .unwrap();
            invalidateBookingTags();
            dispatch(enqueueSuccessSnackbar({ message: t('bookings.archive.snackbar.restore_success') }));
            return true;
        } catch (error) {
            Logger('restoreBooking').error(error);
            dispatch(enqueueErrorSnackbar({ message: t('bookings.archive.snackbar.restore_error') }));
            return false;
        }
    };
};

export const useAnonymizeBooking = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation();

    return async (bookingId: string): Promise<boolean> => {
        try {
            await store
                .dispatch(bookingArchiveApiSlice.endpoints.anonymizeBooking.initiate({ bookingId }))
                .unwrap();
            invalidateBookingTags();
            dispatch(enqueueSuccessSnackbar({ message: t('bookings.archive.snackbar.anonymize_success') }));
            return true;
        } catch (error) {
            Logger('anonymizeBooking').error(error);
            dispatch(enqueueErrorSnackbar({ message: t('bookings.archive.snackbar.anonymize_error') }));
            return false;
        }
    };
};
